import Link from 'components/commercetools-ui/atoms/link';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import Image, { ImageProps } from 'frontastic/lib/image';
import { isStringHtml } from 'helpers/utils/isStringHtml';
import { useMemo } from 'react';
import { Reference } from 'types/reference';

export interface CategoryPreviewsProps {
  headingTextSize?: string;
  headingText: string;
  bodyText?: string;
  bodyTextSize?: string;
  ctaText?: string;
  ctaLink?: Reference;
  ctaDataAnalytics?: string;
  image: ImageProps;
  imagePosition: string;
  backgroundColor: string;
  buttonVariant: 'primary' | 'secondary';
  sectionId?: string;
  height: number; //
}

const CategoryPreviews: React.FC<CategoryPreviewsProps> = ({
  imagePosition,
  image,
  ctaLink,
  ctaText,
  ctaDataAnalytics,
  headingTextSize,
  headingText,
  bodyTextSize,
  bodyText,
  backgroundColor,
  buttonVariant = 'primary',
  sectionId,
}) => {
  const headingClasses = useMemo(() => {
    return `text-${headingTextSize} font-bold text-white`;
  }, [headingTextSize]);

  const bodyClasses = useMemo(() => {
    return `text-${bodyTextSize} text-white mt-1`;
  }, [bodyTextSize]);

  const contextBoxClasses = useMemo(() => {
    const boxPosition = imagePosition === 'left' ? '' : 'lg:right-0';
    return `lg:absolute inset-x-0 top-0 rounded-t-lg lg:rounded-t-none lg:rounded-l-lg bg-[#202020] lg:bg-padi-gray-darkest bg-opacity-80 p-[24px] backdrop-blur backdrop-filter flex lg:inset-x-auto lg:inset-y-0 lg:w-[384px] justify-between flex-col gap-24 lg:items-start lg:rounded-br-none lg:rounded-tl-lg ${boxPosition}`;
  }, [imagePosition]);

  const buttonBgColor = useMemo(() => {
    return buttonVariant === 'primary' ? 'bg-blue-500 hover:bg-blue-700' : 'border border-white lg:border-[#4A4A4B]';
  }, [buttonVariant]);

  return (
    <div className={`bg-${backgroundColor} mx-auto mb-24 mt-64 max-w-7xl px-24 lg:px-32`} id={sectionId}>
      <div className="relative mx-auto flex max-w-2xl flex-col-reverse gap-8 lg:mx-0 lg:block lg:max-w-none">
        <div className="relative h-[320px] overflow-hidden rounded-b-lg lg:rounded-lg">
          <div className="absolute inset-0">
            <Image
              className="h-full w-full object-cover object-center"
              media={image.media}
              alt={image.title}
              priority
            />
          </div>
        </div>
        <div className={contextBoxClasses}>
          <div>
            <h2 className={headingClasses}>{headingText}</h2>
            {bodyText && isStringHtml(bodyText) ? (
              <div className={bodyClasses} dangerouslySetInnerHTML={{ __html: bodyText }} />
            ) : (
              bodyText && <Markdown className={bodyClasses} markdown={bodyText} />
            )}
          </div>
          {ctaLink && ctaText && (
            <Link
              padi-analytics={ctaDataAnalytics}
              className={`flex h-40 flex-shrink-0 items-center justify-center rounded-md px-[16px] py-[12px] text-base font-medium text-white lg:w-full ${buttonBgColor}`}
              link={ctaLink}
            >
              {ctaText}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryPreviews;
